import { Button, Play } from '@core/ui';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Popup } from '../../atoms';

interface Props {
    canEnd: boolean;
    canResume: boolean;
    /** The server connection is up, but the presenter's own video has not come back yet. */
    waitingForMedia: boolean;
    /**
     * The video comes from the presenter's own encoder. Nothing pauses on our side then, and missing video
     * means the encoder stopped sending -- not the browser's connection, which the default text blames.
     */
    externalEncoder: boolean;
    onResume: () => void;
    onClose: () => void;
}

/**
 * How long each phase may take before refreshing is the better advice. The publisher reconnects on its
 * own schedule, and after a long outage it can trail the server connection by half a minute.
 */
const SUGGEST_REFRESH_AFTER_MS = { reconnecting: 5000, waitingForMedia: 30000 } as const;

export const ConnectionIssue = ({ canEnd, canResume, waitingForMedia, externalEncoder, onResume, onClose }: Props) => {
    const { t } = useTranslation();
    const [suggestRefresh, setSuggestRefresh] = useState(false);

    // Each phase starts its own clock. The advice used to latch: once "refresh the page" showed during the
    // outage it stayed after the server was back, while the presenter only had to wait for their video.
    const phase = canResume ? 'ready' : waitingForMedia ? 'waitingForMedia' : 'reconnecting';
    useEffect(() => {
        setSuggestRefresh(false);
        if (phase === 'ready') {
            return;
        }

        const timer = setTimeout(() => {
            setSuggestRefresh(true);
        }, SUGGEST_REFRESH_AFTER_MS[phase]);
        return () => clearTimeout(timer);
    }, [phase]);

    // An encoder's own text where the cause differs. Refreshing the page cannot bring an encoder back, so
    // that advice is only for the server connection.
    const key = (name: 'Text' | 'WaitingForMedia' | 'TextResume') =>
        externalEncoder ? `Broadcaster.Dialog.ConnectionIssue.Encoder.${name}` : `Broadcaster.Dialog.ConnectionIssue.${name}`;
    const showSuggestRefresh = suggestRefresh && (phase === 'reconnecting' || (phase === 'waitingForMedia' && !externalEncoder));

    return (
        <Popup
            title={t('Broadcaster.Dialog.ConnectionIssue.Title')}
            isCloseFunctionality={false}
            hasWarning={true}
            open={true}
            onClose={onClose}
            onConfirm={() => {}}
        >
            <div className="w-full">
                <p className="mb-4">
                    <Trans components={{ strong: <strong className="font-medium" /> }} i18nKey={key('Text')} />
                </p>
                {phase === 'reconnecting' && !showSuggestRefresh && <p className="mb-4">{t('Broadcaster.Dialog.ConnectionIssue.Reconnecting')}</p>}
                {phase === 'waitingForMedia' && !showSuggestRefresh && <p className="mb-4">{t(key('WaitingForMedia'))}</p>}
                {showSuggestRefresh && <p className="mb-4">{t('Broadcaster.Dialog.ConnectionIssue.SuggestRefresh')}</p>}
                <p className="mb-4">{t(key('TextResume'))}</p>
                {/* An encoder broadcast that was live stays live, so there is nothing to resume unless it was paused. */}
                {(!externalEncoder || canResume) && (
                    <Button variant="success" onClick={() => onResume()} classNames="w-full mb-4" disabled={!canResume}>
                        <Play classNames="h-4 fill-current me-3" />
                        {t('Broadcaster.Button.Resume')}
                    </Button>
                )}
                <Button variant="primary-outline" onClick={() => onClose()} classNames="w-full" disabled={!canEnd}>
                    {t('Broadcaster.Dialog.ConnectionIssue.Button.Close')}
                </Button>
            </div>
        </Popup>
    );
};
